import {
	addMonths,
	addQuarters,
	addYears,
	differenceInCalendarMonths,
	differenceInCalendarQuarters,
	differenceInCalendarYears,
	format,
} from "date-fns";

const quarterToStartDate = (quarterString: string): Date => {
	const [year, quarter] = quarterString.split("-");
	const monthMap = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 };
	return new Date(Number(year), monthMap[quarter], 1);
};

// Create a time range based on the labels and datasets assuming the labels are sorted
export const fillTimeRange = (chartDatasets, rangeType) => {
	const { labels = [], datasets = [] } = chartDatasets;

	if (labels.length <= 1 || !rangeType) {
		return { labels, datasets };
	}

	// Special case for invalid labels: Remove the first label if it is null
	if (labels[0]._id === null && !labels[0].name) {
		labels.splice(0, 1);
		datasets.splice(0, 1);
	}

	const startDate =
		rangeType === "quarter" ? quarterToStartDate(labels[0].name as string) : new Date(labels[0].name as string);
	const endDate =
		rangeType === "quarter"
			? quarterToStartDate(labels[labels.length - 1].name as string)
			: new Date(labels[labels.length - 1].name as string);

	const dateRange = getLabelsRange(startDate, endDate, rangeType);

	// map the time labels to the dataset
	const labelsMap = labels.reduce((acc, label, index) => {
		acc[label.name as string] = { ...label, data: datasets[index] };
		return acc;
	}, {});

	// fill the time range with the datasets
	return {
		labels: dateRange.map((date) => labelsMap[date] || { name: date }),
		datasets: dateRange.map((date) => labelsMap[date]?.data),
	};
};

export const getLabelsRange = (startDate: Date, endDate: Date, rangeType: "month" | "year" | "quarter"): string[] => {
	switch (rangeType) {
		case "month":
			return generateMonthlyRange(startDate, endDate);

		case "year":
			return generateYearlyRange(startDate, endDate);

		case "quarter":
			return generateQuarterlyRange(startDate, endDate);

		default:
			throw new Error("Invalid range type. Choose month, year, or quarter.");
	}
};

const generateMonthlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const monthsRange = differenceInCalendarMonths(endDate, startDate) + 1;

	for (let i = 0; i < monthsRange; i++) {
		const currentMonth = addMonths(startDate, i);
		dateRange.push(format(currentMonth, "yyyy-MM"));
	}

	return dateRange;
};

const generateYearlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const yearsCount = differenceInCalendarYears(endDate, startDate) + 1;

	for (let i = 0; i < yearsCount; i++) {
		const currentYear = addYears(startDate, i);
		dateRange.push(format(currentYear, "yyyy"));
	}

	return dateRange;
};

const generateQuarterlyRange = (startDate: Date, endDate: Date) => {
	const dateRange: string[] = [];
	const quartersCount = differenceInCalendarQuarters(endDate, startDate) + 1;

	for (let i = 0; i < quartersCount; i++) {
		const currentQuarter = addQuarters(startDate, i);
		dateRange.push(`${format(currentQuarter, "yyyy")}-Q${Math.floor(currentQuarter.getMonth() / 3 + 1)}`);
	}

	return dateRange;
};
